import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import cookies from 'js-cookie'

import * as S from './styled'
import { VcLetter } from './icons/VcLetter'
import { CashbackSymbol } from './icons/CashbackSymbol'

const formatDate = (isoDate: string | null | undefined) => {
  if (!isoDate) {
    return ''
  }

  const date = new Date(isoDate)

  if (Number.isNaN(date.getTime())) {
    return ''
  }

  return date.toLocaleDateString('pt-BR')
}

type CashbackBlock = { id: string; value: number; expiresAt: string }

type PointsState = {
  value: number
  expirationDate: string | null
}

type Props = {
  mdmClientNumber: number
}

type LoyaltyCounter = {
  type?: {
    value?: string
  }
  value?: number
  expirationDate?: string | null
}

type RewardAttributes = {
  value?: number
  validityEndDate?: string | null
}

type LoyaltyReward = {
  id: string
  engineTemplateCode?: string
  metaModelCode?: string
  modelCode?: string
  status?: string
  attributes?: RewardAttributes
}

type LoyaltyAccount = {
  type?: string
  counters?: LoyaltyCounter[]
  rewards?: LoyaltyReward[]
}

type LoyaltyAccountResponse = {
  data?: {
    data?: LoyaltyAccount
  }
}

const MIN_POINTS_TO_EXCHANGE = 500
const EXCHANGE_DISABLED_TOOLTIP =
  'Você precisa de no mínimo 500 pontos para trocar por cashback'

const Cashback: React.FC<Props> = ({ mdmClientNumber }) => {
  const [cashbackBlocks, setCashbackBlocks] = useState<CashbackBlock[]>([])
  const [points, setPoints] = useState<PointsState | null>(null)

  const [isLoading, setIsLoading] = useState(true)
  const [isProUser, setProUser] = useState(false)

  const [, setIsDrawerOpen] = useState(false)
  const [, setDrawerStartView] = useState<'exchange' | 'help'>('exchange')

  const [isExchangeTooltipOpen, setIsExchangeTooltipOpen] = useState(false)

  const fetchLoyaltyAccount = useCallback(async () => {
    setIsLoading(true)

    try {
      const token = cookies.get('api_token')

      if (!token) {
        return
      }

      const response = await axios.get<LoyaltyAccountResponse>(
        `/api/v2/loyaltyProgram/lys/loyalty-accounts?customer=${mdmClientNumber}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      const account = response.data?.data?.data

      if (!account) {
        setPoints({ value: 0, expirationDate: null })
        setCashbackBlocks([])
        setProUser(false)
        return
      }

      if (Array.isArray(account.counters)) {
        const pointsCounter = account.counters.find(
          counter => counter?.type?.value === 'P'
        )

        setPoints({
          value: pointsCounter?.value ?? 0,
          expirationDate: pointsCounter?.expirationDate ?? null
        })
      } else {
        setPoints({ value: 0, expirationDate: null })
      }

      if (Array.isArray(account.rewards)) {
        const activeCashbacks = account.rewards.filter(reward => {
          return (
            reward.engineTemplateCode === 'voucher_management' &&
            reward.metaModelCode === 'LEGACY_CHECK_CURRENCY' &&
            typeof reward.modelCode === 'string' &&
            reward.modelCode.startsWith('VOUCHER_LMBR') &&
            (reward.status === 'AVAILABLE' || reward.status === 'IN_USE') &&
            reward.attributes != null &&
            typeof reward.attributes.value === 'number' &&
            reward.attributes.value > 0
          )
        })

        const mapped: CashbackBlock[] = activeCashbacks.map(reward => ({
          id: reward.id,
          value: reward.attributes?.value ?? 0,
          expiresAt: formatDate(reward.attributes?.validityEndDate)
        }))

        setCashbackBlocks(mapped)
      } else {
        setCashbackBlocks([])
      }

      setProUser(account.type === '4')
    } catch (error) {
      setPoints({ value: 0, expirationDate: null })
      setCashbackBlocks([])
      setProUser(false)
    } finally {
      setIsLoading(false)
    }
  }, [mdmClientNumber])

  useEffect(() => {
    fetchLoyaltyAccount()
  }, [fetchLoyaltyAccount])

  const openDrawer = (start: 'exchange' | 'help' = 'exchange') => {
    setDrawerStartView(start)
    setIsDrawerOpen(true)
  }

  const isLoadingPoints = isLoading && !points

  const formattedPointsValue =
    points?.value != null
      ? Intl.NumberFormat('pt-BR').format(points.value)
      : '0'

  const formattedPointsExpiration = points?.expirationDate
    ? formatDate(points.expirationDate)
    : ''

  const hasMinPointsToExchange = (points?.value ?? 0) >= MIN_POINTS_TO_EXCHANGE
  const isExchangeDisabled = isLoadingPoints || !hasMinPointsToExchange
  const shouldShowExchangeTooltip =
    !isLoadingPoints && !hasMinPointsToExchange && !isProUser

  const exchangeTooltipId = useMemo(
    () => `cashback-exchange-tooltip-${mdmClientNumber}`,
    [mdmClientNumber]
  )

  return (
    <S.Wrapper>
      <S.Page>
        <S.Header>
          <S.Title>Meu Cashback</S.Title>
          <S.Subtitle>
            Troque seus pontos por cashback e tenha desconto em suas compras
          </S.Subtitle>
        </S.Header>

        <S.Grid>
          <S.PointsCard>
            <S.CardHeader>
              <S.IconCircle>
                <VcLetter />
              </S.IconCircle>

              <div>
                <S.SmallLabel>PONTOS</S.SmallLabel>

                {isLoadingPoints ? (
                  <>
                    <S.ShellLine className="shell-loader" />
                    <S.ShellDoubleLine className="shell-loader" />
                  </>
                ) : (
                  <>
                    <S.Amount>{formattedPointsValue}</S.Amount>
                    {formattedPointsExpiration && (
                      <S.ValidUntil>
                        Válidos até {formattedPointsExpiration}
                      </S.ValidUntil>
                    )}
                  </>
                )}
              </div>
            </S.CardHeader>

            <S.Actions>
              {!isProUser && (
                <span
                  style={{ position: 'relative', display: 'inline-flex' }}
                  onMouseEnter={() => {
                    if (shouldShowExchangeTooltip)
                      setIsExchangeTooltipOpen(true)
                  }}
                  onMouseLeave={() => setIsExchangeTooltipOpen(false)}
                  onFocus={() => {
                    if (shouldShowExchangeTooltip)
                      setIsExchangeTooltipOpen(true)
                  }}
                  onBlur={() => setIsExchangeTooltipOpen(false)}
                  aria-describedby={
                    shouldShowExchangeTooltip ? exchangeTooltipId : undefined
                  }>
                  <S.PrimaryButton
                    type="button"
                    onClick={() => openDrawer('exchange')}
                    disabled={isExchangeDisabled}
                    aria-disabled={isExchangeDisabled}
                    aria-describedby={
                      shouldShowExchangeTooltip ? exchangeTooltipId : undefined
                    }>
                    Trocar por cashback
                  </S.PrimaryButton>

                  {shouldShowExchangeTooltip && isExchangeTooltipOpen && (
                    <S.Tolltip id={exchangeTooltipId} role="tooltip">
                      {EXCHANGE_DISABLED_TOOLTIP}
                      <S.TolltipBullet aria-hidden="true" />
                    </S.Tolltip>
                  )}
                </span>
              )}

              <S.SecondaryButton
                type="button"
                onClick={() => openDrawer('help')}
                disabled={isLoadingPoints}>
                Como usar
              </S.SecondaryButton>
            </S.Actions>
          </S.PointsCard>

          {cashbackBlocks.map(({ id, value, expiresAt }) => (
            <S.CashbackCard key={id}>
              <S.CardHeader>
                <S.IconCircle light>
                  <CashbackSymbol />
                </S.IconCircle>

                <div>
                  <S.SmallLabel>SALDO DO CASHBACK</S.SmallLabel>

                  <S.Amount>
                    <strong>R$</strong>{' '}
                    {Intl.NumberFormat('pt-BR').format(value)}
                  </S.Amount>

                  <S.ValidUntil>Válidos até {expiresAt}</S.ValidUntil>
                </div>
              </S.CardHeader>

              <S.Actions>
                <S.SecondaryButton
                  type="button"
                  style={{ width: '70%' }}
                  onClick={() => openDrawer('help')}>
                  Como usar
                </S.SecondaryButton>
              </S.Actions>
            </S.CashbackCard>
          ))}
        </S.Grid>
      </S.Page>
    </S.Wrapper>
  )
}

export default Cashback
